import React, { useEffect, useRef, useState } from "react";
import { PropsWithoutRef } from "react";
import {
  Drawer,
  Toolbar,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  IconButton,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import { SIDEBAR_WIDTH } from "../constants";
import MenuIcon from "@mui/icons-material/Menu";
import useAuth from "../hooks/useAuth";
import { LOGIN_USER } from "../utils/enums";
import { startSignalRConnection } from "../utils/socket";
import { getFromStorage, setToStorage, STORAGE_KEYS } from "../helpers";
interface Props {
  mobileOpen: boolean;
  handleSidebarToggle: () => void;
}

const menuItems = [
  {
    items: [
      {
        label: "Dashboard",
        icon: "/static/images/dashboard_icon.svg",
        path: "/dashboard",
        selectedPaths: ["/dashboard"],
      },
    ],
  },
  {
    section: "Servicing",
    items: [
      {
        label: "Booking & RSVP Tracker",
        icon: "/static/images/sidebar_icon5.svg",
        path: "/manage-booking-resvp",
        selectedPaths: [
          "/manage-booking-resvp",
          "/manage-booking-resvp/booking-detail",
        ],
      },
      {
        label: "Cancellation Requests",
        icon: "/static/images/sidebar_icon14.svg",
        path: "/manage-cancellation",
        selectedPaths: ["/manage-cancellation"],
      },
    ],
  },
  {
    section: "Offers Management",
    items: [
      {
        label: "Offer",
        icon: "/static/images/sidebar_icon3.svg",
        path: "/manage-offer",
        selectedPaths: [
          "/manage-offer/build-offer",
          "/manage-offer",
          "/manage-offer/preview-offer",
          "/manage-offer/preview-offer-detail",
        ],
      },
      {
        label: "Category",
        icon: "/static/images/category_icon.svg",
        path: "/manage-categories",
        selectedPaths: ["/manage-categories"],
      },
      {
        label: "Group & Tags",
        icon: "/static/images/sidebar_icon6.svg",
        path: "/manage-groups",
        selectedPaths: ["/manage-groups", "/manage-groups/tags"],
      },
      {
        label: "Butterfly Effect",
        icon: "/static/images/sidebar_icon4.svg",
        path: "/manage-butterfly-effect",
        selectedPaths: ["/manage-butterfly-effect"],
      },
    ],
  },
  {
    section: "User Management",
    items: [
      {
        label: "Members",
        icon: "/static/images/sidebar_icon2.svg",
        path: "/manage-customers",
        selectedPaths: [
          "/manage-customers",
          "/manage-customers/detail",
          "/manage-customers/add",
        ],
      },
      {
        label: "Groups",
        icon: "/static/images/Group.svg",
        path: "/groups",
        selectedPaths: ["/manage-cms"],
      },
    ],
  },
  {
    section: "Employee Management",
    items: [
      {
        label: "Add Employee",
        icon: "/static/images/employee_icon.svg",
        path: "/manage-employee",
        selectedPaths: [
          "/manage-employee",
          "/manage-employee/add",
          "/manage-employee/view",
        ],
      },
      {
        label: "Manage Sub-admin",
        icon: "/static/images/sidebar_icon11.svg",
        path: "/manage-subadmin",
        selectedPaths: [
          "/manage-subadmin",
          "/manage-subadmin/add",
          "/manage-subadmin/detail",
          "/manage-subadmin/roles",
          "/manage-subadmin/roles/add",
          "/manage-subadmin/roles/detail",
        ],
      },
    ],
  },
  {
    section: "Subscriptions & Payments",
    items: [
      {
        label: "Manage Subscriptions",
        icon: "/static/images/sidebar_icon13.svg",
        path: "/manage-subscription",
        selectedPaths: [
          "/manage-subscription",
          "/manage-subscription/add",
          "/manage-subscription/view",
        ],
      },
      {
        label: "Transactions",
        icon: "/static/images/sidebar_icon14.svg",
        path: "/transactions",
        selectedPaths: ["/transactions"],
      },
      // {
      //   label: "Payments",
      //   icon: "/static/images/sidebar_icon14.svg",
      //   path: "/manage-payments",
      //   selectedPaths: ["/manage-payments"],
      // },
    ],
  },
  {
    section: "Reports and Analytics",
    items: [
      {
        label: "Reports",
        icon: "/static/images/sidebar_icon13.svg",
        path: "/revenue-analytics",
      },
    ],
  },
  {
    section: "Broadcast",
    items: [
      {
        label: "Notification & Email",
        icon: "/static/images/sidebar_icon8.svg",
        path: "/manage-notifications",
        selectedPaths: ["/manage-notifications"],
      },
    ],
  },
  {
    section: "Manage CMS Pages",
    items: [
      {
        label: "Contact Support",
        icon: "/static/images/ContactSupport.svg",
        path: "/manage-cms?tab=0",
        selectedPaths: ["/manage-cms"],
      },
      {
        label: "Terms & Conditions",
        icon: "/static/images/Legal.svg",
        path: "/manage-cms?tab=1",
        selectedPaths: ["/manage-cms"],
      },
      {
        label: "Privacy Policy",
        icon: "/static/images/PrivacyPolicy.svg",
        path: "/manage-cms?tab=2",
        selectedPaths: ["/manage-cms"],
      },
      {
        label: "About Us",
        icon: "/static/images/PrivacyPolicy.svg",
        path: "/manage-cms?tab=3",
        selectedPaths: ["/manage-cms"],
      },
      {
        label: "Cancellation Policy",
        icon: "/static/images/PrivacyPolicy.svg",
        path: "/manage-cms?tab=4",
        selectedPaths: ["/manage-cms"],
      },
      {
        label: "FAQ",
        icon: "/static/images/FAQ.svg",
        path: "/manage-cms?tab=5",
        selectedPaths: ["/manage-cms"],
      },
      {
        label: "Expertise",
        icon: "/static/images/Expertise.svg",
        path: "/manage-cms?tab=6",
        selectedPaths: ["/manage-cms"],
      },
      {
        label: "Interest",
        icon: "/static/images/Expertise.svg",
        path: "/manage-cms?tab=7",
        selectedPaths: ["/manage-cms"],
      },
    ],
  },
];

export default function Sidebar({
  mobileOpen,
  handleSidebarToggle,
}: PropsWithoutRef<Props>) {
  const location = useLocation();
  const navigate = useNavigate();
  const listRef = useRef<any | null>(null);
  const userData = useAuth();
  const countData = Number(getFromStorage(STORAGE_KEYS.bookingCount)) || 0;

  const [bookingCount, setBookingCount] = useState(0);

  const hasViewPermission = (label: string) => {
    // Show everything if permissions are empty
    if (!userData?.permissions || userData?.permissions?.length === 0)
      return true;

    // Special case for Manage CMS Pages
    if (
      label === "Contact Support" ||
      label === "Terms & Conditions" ||
      label === "Privacy Policy" ||
      label === "About Us" ||
      label === "Cancellation Policy" ||
      label === "FAQ" ||
      label === "Expertise" ||
      label === "Interest"
    ) {
      return userData?.permissions?.some(
        (perm: any) => perm?.moduleName === "Manage CMS Pages" && perm?.view
      );
    }

    // Normal case
    return userData?.permissions?.some(
      (perm: any) => perm?.moduleName === label && perm?.view
    );
  };

  // Filter menu items based on user type
  const getFilteredMenuItems = () => {
    if (userData?.userType === LOGIN_USER.EMPLOYEE) {
      return [
        {
          items: [
            {
              label: "Dashboard",
              icon: "/static/images/dashboard_icon.svg",
              path: "/dashboard",
              selectedPaths: ["/dashboard"],
            },
          ],
        },
        {
          section: "Servicing",
          items: [
            {
              label: "Booking & RSVP Tracker",
              icon: "/static/images/sidebar_icon5.svg",
              path: "/manage-booking-resvp",
              selectedPaths: [
                "/manage-booking-resvp",
                "/manage-booking-resvp/booking-detail",
              ],
            },
            {
              label: "Cancellation Requests",
              icon: "/static/images/sidebar_icon14.svg",
              path: "/manage-cancellation",
              selectedPaths: ["/manage-cancellation"],
            },
          ],
        },
      ];
    }
    return menuItems;
  };

  const filteredMenuItems = getFilteredMenuItems()
    ?.map((group) => ({
      ...group,
      items: group?.items?.filter((item) => hasViewPermission(item?.label)), // filter items
    }))
    ?.filter((group) => group?.items?.length > 0);

  useEffect(() => {
    if (listRef?.current) {
      listRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [location.search, location.pathname]);

  useEffect(() => {
    const init = async () => {
      const connection = await startSignalRConnection(
        `https://devbutterflyapi.appgrowthcompany.com/membershipHub?userId=${userData?.id}&userType=admin`
      );
      const handleBookingCount = (data: any) => {
        setBookingCount(data);
        setToStorage(STORAGE_KEYS.bookingCount, data);
      };

      connection?.on("ReceiveBookingCount", handleBookingCount);

      // cleanup
      return () => {
        connection?.off("ReceiveBookingCount", handleBookingCount);
      };
    };

    init();
  }, []);

  useEffect(() => {
    setBookingCount(countData);
  }, [countData]);

  const drawer = (
    <>
      <Toolbar className="sidebar-logo" sx={{ position: "relative" }}>
        <figure>
          <img
            src="/static/images/butterfly_logo.svg"
            alt=""
            onClick={() => navigate("/dashboard")}
          />
        </figure>

        <IconButton
          sx={{
            position: "absolute",
            top: "14px",
            right: "10px",
            color: "white",
          }}
          onClick={handleSidebarToggle}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>
      <div className="s_head text_white">
        <h3>MAIN MENU</h3>
      </div>

      <List sx={{ flexGrow: 1 }} className="sidebr-lst">
        {filteredMenuItems?.map((group, sectionIndex) => (
          <React.Fragment key={sectionIndex}>
            <h2 className="subTitle">{group.section}</h2>

            {group.items.map((item, index) => (
              <ListItemButton
                key={index}
                className="lst-itm"
                selected={location.pathname + location.search === item.path}
                onClick={() => item.path && navigate(item.path)}
                disableRipple
                ref={
                  location.pathname + location.search === item.path
                    ? listRef
                    : null
                }
              >
                <ListItemIcon>
                  <img src={item.icon} alt="Icon" />
                </ListItemIcon>
                <ListItemText
                  className="lstitm-txt"
                  primary={
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <span>{item.label}</span>
                      {item.label === "Booking & RSVP Tracker" &&
                        bookingCount > 0 && (
                          <Box
                            component="span"
                            sx={{
                              backgroundColor: "#FF6B6B",
                              color: "#fff",
                              fontSize: "12px",
                              borderRadius: "50%",
                              padding: "2px 8px",
                              marginLeft: "8px",
                              fontWeight: 600,
                              width: "22px",
                              height: "22px",
                              textAlign: "center",
                            }}
                          >
                            {bookingCount}
                          </Box>
                        )}
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
          </React.Fragment>
        ))}
      </List>
    </>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: mobileOpen ? SIDEBAR_WIDTH : 0,
        transition: "width 0.3s",
        overflowX: "hidden",
      }}
      className={mobileOpen ? "sidebar-main-div active" : "sidebar-main-div"}
    >
      <Drawer
        className="sidebar_drawer"
        variant="persistent"
        sx={{
          display: { xs: "none", lg: "block" },
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
          },
        }}
        open
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        className="sidebar_drawer"
        sx={{
          display: { xs: "none", lg: "block" },
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
}
